import { ButtonHTMLAttributes, FC, RefObject, useRef } from 'react';
import { classNames } from 'primereact/utils';
import { Tooltip, TrashIcon } from '@repo/atomic-components';
import { CASE_DELETE_BLOCKED_TOOLTIP } from '@/constants';
import { TableIconButton } from './TableIconButton';

interface DeleteDocumentButtonProps extends ButtonHTMLAttributes<HTMLButtonElement> {
  /** Case is mid AI processing — disables the button and explains why on hover. */
  isBlocked?: boolean;
}

/**
 * Trash chip used in the document action columns (case page + medical
 * records list). When `isBlocked` the button is disabled and a PrimeReact
 * tooltip replaces the native `title`, so the reason appears immediately
 * instead of after the browser's ~1s `title` delay — and next to the button
 * rather than under the not-allowed cursor.
 */
export const DeleteDocumentButton: FC<DeleteDocumentButtonProps> = ({
  isBlocked = false,
  title,
  onClick,
  className,
  ...rest
}) => {
  const wrapperRef = useRef<HTMLSpanElement>(null);

  return (
    <>
      <span ref={wrapperRef} className={classNames('inline-flex shrink-0', isBlocked && 'cursor-not-allowed')}>
        <TableIconButton
          {...rest}
          title={isBlocked ? undefined : title}
          disabled={isBlocked}
          onClick={isBlocked ? undefined : onClick}
          // Disabled buttons swallow hover events, so let them through to the
          // wrapper the tooltip is anchored to.
          className={classNames(isBlocked && 'pointer-events-none opacity-50', className)}
        >
          <TrashIcon className="[&>path]:fill-fletchers-black-900" />
        </TableIconButton>
      </span>
      {isBlocked && (
        <Tooltip
          target={wrapperRef as RefObject<HTMLElement>}
          content={CASE_DELETE_BLOCKED_TOOLTIP}
          position="left"
          showDelay={0}
          hideDelay={0}
        />
      )}
    </>
  );
};
